import React from 'react';
import {
  Modal,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { SavedWorkspaceState } from '../../types';

interface PropertiesDialogProps {
  isOpen: boolean;
  savedState: SavedWorkspaceState | null;
  onClose: () => void;
}

export const PropertiesDialog: React.FC<PropertiesDialogProps> = ({
  isOpen,
  savedState,
  onClose,
}) => {
  if (!savedState) return null;

  const formatDate = (date: Date) => {
    return date.toLocaleString();
  };

  const formatSize = (obj: unknown) => {
    const jsonStr = JSON.stringify(obj);
    const bytes = new Blob([jsonStr]).size;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      title={
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
        }}>
          <InfoCircleOutlined style={{ fontSize: '20px' }} />
          Properties: {savedState.name}
        </div>
      }
      footer={null}
      width={500}
    >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          
          {/* Basic Info */}
          <div>
            <h4 style={{ 
              margin: '0 0 0.5rem 0', 
              fontSize: 'var(--font-size-md)', 
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--color-text-primary)'
            }}>
              Basic Information
            </h4>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '120px 1fr', 
              gap: '0.5rem',
              fontSize: 'var(--font-size-md)'
            }}>
              <span style={{ color: 'var(--color-text-secondary)' }}>Name:</span>
              <span style={{ color: 'var(--color-text-primary)', fontWeight: 'var(--font-weight-medium)' }}>{savedState.name}</span>
              
              <span style={{ color: 'var(--color-text-secondary)' }}>Version:</span>
              <span style={{ color: 'var(--color-text-primary)' }}>{savedState.stateVersion}</span>
              
              <span style={{ color: 'var(--color-text-secondary)' }}>Workspace ID:</span>
              <span style={{ 
                color: 'var(--color-text-primary)', 
                fontFamily: 'monospace',
                fontSize: 'var(--font-size-sm)'
              }}>
                {savedState.workspaceId}
              </span>
              
              {savedState.description && (
                <>
                  <span style={{ color: 'var(--color-text-secondary)' }}>Description:</span>
                  <span style={{ color: 'var(--color-text-primary)' }}>{savedState.description}</span>
                </>
              )}
            </div>
          </div>

          {/* Timestamps */}
          <div>
            <h4 style={{ 
              margin: '0 0 0.5rem 0', 
              fontSize: 'var(--font-size-md)', 
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--color-text-primary)'
            }}>
              Timeline
            </h4>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '120px 1fr', 
              gap: '0.5rem',
              fontSize: 'var(--font-size-md)'
            }}>
              <span style={{ color: 'var(--color-text-secondary)' }}>Created:</span>
              <span style={{ color: 'var(--color-text-primary)' }}>{formatDate(savedState.createdAt)}</span>
              
              <span style={{ color: 'var(--color-text-secondary)' }}>Last Updated:</span>
              <span style={{ color: 'var(--color-text-primary)' }}>{formatDate(savedState.updatedAt)}</span>
              
              {savedState.lastAccessedAt && (
                <>
                  <span style={{ color: 'var(--color-text-secondary)' }}>Last Accessed:</span>
                  <span style={{ color: 'var(--color-text-primary)' }}>{formatDate(savedState.lastAccessedAt)}</span>
                </>
              )}
            </div>
          </div>

          {/* Chat Configuration */}
          {savedState.chatSettings && (
            <div>
              <h4 style={{ 
                margin: '0 0 0.5rem 0', 
                fontSize: 'var(--font-size-md)', 
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-text-primary)'
              }}>
                Configuration
              </h4>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '120px 1fr', 
                gap: '0.5rem',
                fontSize: 'var(--font-size-md)'
              }}>
                {savedState.chatSettings.model && (
                  <>
                    <span style={{ color: 'var(--color-text-secondary)' }}>Model:</span>
                    <span style={{ color: 'var(--color-text-primary)', fontFamily: 'monospace', fontSize: 'var(--font-size-sm)' }}>
                      {savedState.chatSettings.model}
                    </span>
                  </>
                )}
                
                {typeof savedState.chatSettings.temperature === 'number' && (
                  <>
                    <span style={{ color: 'var(--color-text-secondary)' }}>Temperature:</span>
                    <span style={{ color: 'var(--color-text-primary)' }}>
                      {savedState.chatSettings.temperature}
                    </span>
                  </>
                )}
                
                {savedState.chatSettings.domain && (
                  <>
                    <span style={{ color: 'var(--color-text-secondary)' }}>Domain:</span>
                    <span style={{ color: 'var(--color-text-primary)', textTransform: 'capitalize' }}>
                      {savedState.chatSettings.domain}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Content Info */}
          <div>
            <h4 style={{ 
              margin: '0 0 0.5rem 0', 
              fontSize: 'var(--font-size-md)', 
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--color-text-primary)'
            }}>
              Content
            </h4>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '120px 1fr', 
              gap: '0.5rem',
              fontSize: 'var(--font-size-md)'
            }}>
              <span style={{ color: 'var(--color-text-secondary)' }}>State Size:</span>
              <span style={{ color: 'var(--color-text-primary)' }}>{formatSize(savedState.stateData)}</span>
              
              {savedState.chatMessages && (
                <>
                  <span style={{ color: 'var(--color-text-secondary)' }}>Messages:</span>
                  <span style={{ color: 'var(--color-text-primary)' }}>
                    {Array.isArray(savedState.chatMessages) ? savedState.chatMessages.length : 0} messages
                  </span>
                </>
              )}
              
              {savedState.sessionId && (
                <>
                  <span style={{ color: 'var(--color-text-secondary)' }}>Session ID:</span>
                  <span style={{ 
                    color: 'var(--color-text-primary)', 
                    fontFamily: 'monospace',
                    fontSize: 'var(--font-size-sm)'
                  }}>
                    {savedState.sessionId}
                  </span>
                </>
              )}
            </div>
          </div>

        </div>
    </Modal>
  );
};